import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Icon,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiArrowRight, FiCheckCircle } from "react-icons/fi";
import { formatPhone } from "../../utils/failedNumbers";
import { NUMBER_STATUSES } from "../../utils/numberStatus";
import { sortChanges } from "../../utils/numberHealthChanges";
import { attachPatients, providerName } from "./FailedNumbersPanel";

// Result of "Refresh DB" in the SMS Status modal: which numbers the rebuild of
// sms_number_health changed, status changes first (see utils/numberHealthChanges).

const SHOW_STEP = 10;

const StatusBadge = ({ status }) => {
  const s = NUMBER_STATUSES[status] || NUMBER_STATUSES.untexted;
  return (
    <Badge colorScheme={s.colorScheme} variant="subtle" borderRadius="full" px={2} flexShrink={0}>
      {s.label}
    </Badge>
  );
};

const ChangeRow = ({ change, providerLabel }) => {
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const accent = useColorModeValue(
    `${(NUMBER_STATUSES[change.to] || NUMBER_STATUSES.untexted).colorScheme}.300`,
    `${(NUMBER_STATUSES[change.to] || NUMBER_STATUSES.untexted).colorScheme}.500`,
  );
  const names = change.patient_names || [];
  return (
    <Box
      data-testid="number-change"
      borderWidth="1px"
      borderColor={border}
      borderLeftWidth="3px"
      borderLeftColor={change.statusChanged ? accent : border}
      borderRadius="md"
      px={3}
      py={2}
    >
      <Flex justify="space-between" align="center" gap={2} wrap="wrap">
        <Box minW={0}>
          <HStack spacing={2} wrap="wrap">
            <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
              {names.length ? names[0] : formatPhone(change.phone_number)}
              {names.length > 1 && (
                <Text as="span" fontWeight="normal" color="gray.500">
                  {" "}
                  +{names.length - 1} more
                </Text>
              )}
            </Text>
            {providerLabel && (
              <Badge variant="outline" colorScheme="gray" borderRadius="full" px={2}>
                {providerLabel}
              </Badge>
            )}
          </HStack>
          {names.length > 0 && (
            <Text fontSize="xs" color="gray.500" fontFamily="mono">
              {formatPhone(change.phone_number)}
            </Text>
          )}
        </Box>
        <HStack spacing={1.5} flexShrink={0}>
          {change.statusChanged ? (
            <>
              <StatusBadge status={change.from} />
              <Icon as={FiArrowRight} color="gray.400" boxSize={3.5} />
              <StatusBadge status={change.to} />
            </>
          ) : (
            <StatusBadge status={change.to} />
          )}
        </HStack>
      </Flex>
      <Text fontSize="xs" color="gray.500" mt={1}>
        {change.notes.join(" · ")}
      </Text>
    </Box>
  );
};

const ChangeList = ({ title, changes, homeSchema, multiProvider }) => {
  const [shown, setShown] = useState(SHOW_STEP);
  if (!changes.length) return null;
  return (
    <Box>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={1.5}>
        {title} ({changes.length})
      </Text>
      <Stack spacing={1.5}>
        {changes.slice(0, shown).map((c) => (
          <ChangeRow
            key={`${c.schema_name}:${c.phone_number}`}
            change={c}
            providerLabel={multiProvider ? providerName(c.schema_name, homeSchema) : null}
          />
        ))}
        {shown < changes.length && (
          <Button size="xs" variant="ghost" onClick={() => setShown(changes.length)}>
            Show all {changes.length}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const NumberHealthChanges = ({ result, homeSchema, onClose, loadNames = attachPatients }) => {
  const bg = useColorModeValue("gray.50", "whiteAlpha.50");
  const border = useColorModeValue("gray.200", "whiteAlpha.200");
  const sorted = useMemo(() => sortChanges(result?.changes), [result]);
  const [named, setNamed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setNamed(null);
    if (!sorted.length) return undefined;
    loadNames(sorted)
      .then((rows) => !cancelled && setNamed(rows))
      .catch(() => !cancelled && setNamed(null));
    return () => {
      cancelled = true;
    };
  }, [sorted, loadNames]);

  if (!result) return null;
  const rows = named || sorted;
  const results = result.results || [];
  const failed = results.filter((r) => r.error);
  const unknown = results.filter((r) => !r.error && !r.changes_known);
  const multiProvider = new Set(rows.map((c) => c.schema_name)).size > 1;
  const statusChanges = rows.filter((c) => c.statusChanged);
  const otherChanges = rows.filter((c) => !c.statusChanged);
  const total = result.total_changes ?? rows.length;

  return (
    <Box data-testid="number-health-changes" bg={bg} borderWidth="1px" borderColor={border} borderRadius="lg" p={3} mb={4}>
      <Flex justify="space-between" align="start" gap={2} mb={2}>
        <Box>
          <Text fontWeight="bold" fontSize="sm">
            Refresh results
          </Text>
          <Text fontSize="xs" color="gray.500">
            {result.numbers ?? 0} numbers checked · {total} changed
            {statusChanges.length ? ` · ${statusChanges.length} changed status` : ""}
            {result.timestamp ? ` · ${new Date(result.timestamp).toLocaleTimeString()}` : ""}
          </Text>
        </Box>
        <CloseButton size="sm" onClick={onClose} aria-label="Hide refresh results" />
      </Flex>

      <Stack spacing={3}>
        {failed.length > 0 && (
          <Alert status="warning" borderRadius="md" py={2}>
            <AlertIcon />
            <AlertDescription fontSize="sm">
              Not refreshed: {failed.map((r) => `${r.schema} (${r.error})`).join(", ")}
            </AlertDescription>
          </Alert>
        )}
        {unknown.length > 0 && (
          <Alert status="info" borderRadius="md" py={2}>
            <AlertIcon />
            <AlertDescription fontSize="sm">
              Refreshed, but the changes could not be read for {unknown.map((r) => r.schema).join(", ")}.
            </AlertDescription>
          </Alert>
        )}
        {total === 0 && unknown.length < results.length - failed.length && (
          <HStack spacing={2} color="gray.500" data-testid="no-number-changes">
            <Icon as={FiCheckCircle} color="green.400" />
            <Text fontSize="sm">No number changed. Everything was already up to date.</Text>
          </HStack>
        )}
        <ChangeList
          title="Status changed"
          changes={statusChanges}
          homeSchema={homeSchema}
          multiProvider={multiProvider}
        />
        <ChangeList
          title="Same status, new activity"
          changes={otherChanges}
          homeSchema={homeSchema}
          multiProvider={multiProvider}
        />
        {total > rows.length && (
          <Text fontSize="xs" color="gray.500">
            Showing the first {rows.length} of {total} changed numbers.
          </Text>
        )}
      </Stack>
    </Box>
  );
};

export default NumberHealthChanges;
